import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize, map, shareReplay } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { environment } from '../../projects/my-app/src/environments/environment';

/**
 * CustomConfigService
 *
 * This service extends the shared ConfigService to allow project-specific overrides.
 * It is pre-built and placed in the base project so developers can modify it if needed.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomConfigService extends ConfigService {
  constructor(http: HttpClient) {
    super(http);
  }

  override getConfig(
    url: string,
    data?: any,
    filters?: any,
    method?: string,
    isTable = false,
  ): Observable<any> {
    if (!data?.customPayload) {
      return super.getConfig(url, data, filters, method, isTable);
    }

    const requestHeaders = this.resolveCustomHeaders(data, filters);
    const queryParamsForHttp = { ...(filters?.queryParams || {}) };
    const rawQueryString = filters?.rawQueryString;
    const requestMethod = String(method || data?.method || 'post').toLowerCase();
    let finalUrl = url;
    let queryParams = new HttpParams();

    const useQueryParams = filters?.useQueryParams === true || data?.useQueryParam === true;
    if (useQueryParams) {
      if (rawQueryString) {
        finalUrl = `${finalUrl}?${rawQueryString}`;
      } else {
        queryParams = this.buildQueryParams(queryParamsForHttp);
      }
    }

    const isLocalAssets = finalUrl.startsWith('assets') || finalUrl.startsWith('/assets');
    if (!isLocalAssets) {
      const baseUrl = environment.URL.endsWith('/')
        ? environment.URL.slice(0, -1)
        : environment.URL;
      const path = finalUrl.startsWith('/') ? finalUrl : `/${finalUrl}`;
      finalUrl = `${baseUrl}${path}`;
    }

    const payload = this.buildScreenCustomPayload(data, filters);
    const dedupKey = `CUSTOM|${requestMethod.toUpperCase()}|${finalUrl}|${JSON.stringify(payload)}|${queryParams.toString()}`;
    if (this.inProgressRequests.has(dedupKey)) {
      return this.inProgressRequests.get(dedupKey)!;
    }

    const req$ = (
      requestMethod === 'post'
        ? this.http.post<any>(finalUrl, payload, { params: queryParams, headers: requestHeaders })
        : this.http.get<any>(finalUrl, { params: queryParams, headers: requestHeaders })
    ).pipe(
      map((res) => this.unwrapResponse(res)),
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => this.inProgressRequests.delete(dedupKey)),
    );

    this.inProgressRequests.set(dedupKey, req$);
    return req$;
  }

  /**
   * Pass-through for getApi if needed, or override similarly
   */
  override getApi(
    url: string,
    filters?: any,
    data?: any,
    method = 'post',
  ): Observable<any> {
    console.groupCollapsed(
      '%c[CustomConfigService] getApi called',
    );
    // ---------------------------------------------------------
    // CUSTOM PAYLOAD OVERRIDE EXAMPLE
    // If you want a COMPLETELY different payload structure (bypassing the Standard Base Payload),
    // intercept it here using the widgetId.
    //
    // Trigger this by setting "customPayload": true in your widget config.
    // ---------------------------------------------------------
    if (data?.customPayload){
        if (data?.widgetId === 'widget2') {
        const myUniquePayload = {
            // DEFINE YOUR CUSTOM PAYLOAD HERE
            key: 'value',
            specialData: true
        };
        return this.http.post(url, myUniquePayload);
    }

    }

    // ---------------------------------------------------------

    return super.getApi(url, filters, data);
  }

  private buildScreenCustomPayload(data?: any, filters?: any): Record<string, any> {
    const explicitCustomPayload =
      data?.customPayload && typeof data.customPayload === 'object' && !Array.isArray(data.customPayload)
        ? { ...data.customPayload }
        : {};
    const filteredRequestParams = this.cleanRequestParams(filters?.requestParams);
    const excludeKeys = new Set(data?.excludeFilterKeys || []);
    const filteredWithExclusions = Object.fromEntries(
      Object.entries(filteredRequestParams).filter(([key]) => !excludeKeys.has(key))
    );
    const defaultRequestData =
      data?.defaultRequestData || data?.defaultRequesData || data?.defaultPayload || {};
    const hasFilteredRequestParams = Object.keys(filteredWithExclusions).length > 0;
    const { operation, batchId, ...customRequestData } = explicitCustomPayload;

    const payload: Record<string, any> = {
      requestData: {
        ...(!hasFilteredRequestParams ? defaultRequestData : {}),
        ...filteredWithExclusions,
        ...customRequestData,
      },
      responseList: [],
      entity: data?.entity || 'default_entity',
      workflowId: data?.workflowId || 'default_workflow',
      endpoints: [
        {
          functionName: data?.functionName || 'DefaultService',
          version: data?.version || '1.0',
        },
      ],
      communicationType: 'rest',
      version: data?.version || '1.0',
    };

    const resolvedOperation = data?.operation ?? operation;
    const resolvedBatchId = data?.batchId ?? batchId;
    if (resolvedOperation !== undefined && resolvedOperation !== null && resolvedOperation !== '') {
      payload['operation'] = resolvedOperation;
    }
    if (resolvedBatchId !== undefined && resolvedBatchId !== null && resolvedBatchId !== '') {
      payload['batchId'] = resolvedBatchId;
    }

    return payload;
  }

  private resolveCustomHeaders(data?: any, filters?: any): Record<string, string> {
    const providedHeaders = {
      ...this.normalizeHeaders(data?.headers),
      ...this.normalizeHeaders(filters?.headers),
    };

    if (!providedHeaders['X-TENANT-ID'] && !providedHeaders['x-tenant-id']) {
      providedHeaders['X-TENANT-ID'] = 'tenantA';
    }

    return providedHeaders;
  }

  private normalizeHeaders(value: any): Record<string, string> {
    if (!value) {
      return {};
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(value).reduce<Record<string, string>>((acc, [key, headerValue]) => {
        acc[key] = String(headerValue);
        return acc;
      }, {});
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      const match = trimmed.match(/"?([^":]+)"?\s*:\s*"?([^"]+)"?/);
      if (match) {
        return { [match[1].trim()]: match[2].trim() };
      }
    }

    return {};
  }
}
